import React from 'react';
import AnalyticsRequestFilter from '../../../models/analytics/AnalyticsRequestFilter';
import ApplicationTrackingContainer from '../../../state/containers/ApplicationTrackingContainer';
import SecondaryLoader from '../../../components/general/SecondaryLoader';
import { css } from '@emotion/css';
import ContentNode from '../../../components/analytics/tracking/ContentNode';
import { ConversationTrackingTreeBaseNode, ConversationTrackingTreeContentNode } from '../../../models/analytics/api/ConversationEventTrackingTreeNode';
import SessionStartNode from '../../../components/analytics/tracking/SessionStartNode';
import SessionEndNode from '../../../components/analytics/tracking/SessionEndNode';
import { color_colors_ocean, color_shades_lighter, color_text_light } from '../../../constants/colors';
import { style_border_default } from '../../../constants/stylesValues';
import CustomScrollbars from '../../../components/structure/CustomScrollbars';
import DownTreePaths from '../../../components/analytics/tracking/DownTreePaths';
import SlimGenericContentItemModel from '../../../models/features/api/SlimGenericContentItemModel';
import NodeOffset from '../../../models/analytics/NodeOffset';
import TreeRowPager from '../../../components/analytics/TreeRowPager';
import _ from 'lodash';
import ConversationScript from '../../conversationScript';
import AnalyticsEmptyView from '../../../components/analytics/AnalyticsEmptyView';

interface ApplicationTrackPageProps {
    applicationId: string
    trackingContainer: ApplicationTrackingContainer
    filter: AnalyticsRequestFilter
    history?: any
    isSpark: boolean
}
interface ApplicationTrackPageState {
    selectedNodes: ConversationTrackingTreeBaseNode[]
    positionOffsets: NodeOffset[]
}


class ApplicationTrackingPage extends React.Component<ApplicationTrackPageProps, ApplicationTrackPageState> {
    constructor(props) {
        super(props);
        this.state = {
            selectedNodes: [],
            positionOffsets: []
        }
    }
    componentDidMount() {
        this.reloadData();
    }
    componentDidUpdate(previousProps: ApplicationTrackPageProps) {
        if (JSON.stringify(previousProps.filter) != JSON.stringify(this.props.filter)) {
            this.reloadData();
        }
    }
    async reloadData() {
        const result = await this.props.trackingContainer.loadTree(this.props.applicationId, this.props.filter);
        if (result?.resultType == "Ok") {
            await this.props.trackingContainer.loadChildNodeDetails(this.props.applicationId, this.props.filter, result.data)
        }
        const tree = this.props.trackingContainer.findTree(this.props.applicationId, this.props.filter);
        if (tree?.data?.childNodes?.length > 3) {
            // check to see if we should set an offset
            // if we have 4 or more, then set the default to far left
            let levelOffset = this.state.positionOffsets.find(l => l.level == 0);
            const index = this.state.positionOffsets.indexOf(levelOffset);
            if (levelOffset) {
                levelOffset.offset = -(tree.data.childNodes.length / 2) + 2;
                this.state.positionOffsets[index] = levelOffset
            } else {
                levelOffset = { level: 0, offset: -(tree.data.childNodes.length / 2) + 2 }
                this.state.positionOffsets.push(levelOffset)
            }
        }
        this.setState({
            ...this.state,
            selectedNodes: [],
            positionOffsets: this.state.positionOffsets
        })
    }
    handleContentNodeClick(node: ConversationTrackingTreeContentNode, level: number) {
        let { selectedNodes, positionOffsets } = this.state;
        if ((selectedNodes[level] as any)?.contentId == node.contentId) {
            selectedNodes = selectedNodes.slice(0, level);
        }
        else {
            selectedNodes = selectedNodes.slice(0, level);
            selectedNodes[level] = node;
            if (node.childNodes?.length > 3) {
                // check to see if we should set an offset
                // if we have 4 or more, then set the default to far left
                let levelOffset = positionOffsets.find(l => l.level == level + 1);
                const index = positionOffsets.indexOf(levelOffset);
                if (levelOffset) {
                    levelOffset.offset = -(node.childNodes.length / 2) + 2;
                    positionOffsets[index] = levelOffset
                } else {
                    levelOffset = { level: level + 1, offset: -(node.childNodes.length / 2) + 2 }
                    positionOffsets.push(levelOffset)
                }

            }
        }
        this.setState({
            ...this.state,
            selectedNodes,
            positionOffsets
        })
        this.props.trackingContainer.loadChildNodeDetails(this.props.applicationId, this.props.filter, node)
    }
    handleEndNodeClick(level: number, node: ConversationTrackingTreeBaseNode) {
        var selectedNodes = this.state.selectedNodes;
        if (selectedNodes[level]?.conversationTrackingNodeType == 'SessionEnd') {
            selectedNodes = selectedNodes.slice(0, level);
        }
        else {
            selectedNodes[level] = node;
        }
        this.setState({
            ...this.state,
            selectedNodes
        })
    }
    handleStartNodeClick() {
        this.setState({
            ...this.state,
            selectedNodes: []
        })
    }
    handlePage(level: number, increment: boolean, nodeLength: number) {

        const positionOffsets = this.state.positionOffsets;
        let levelOffset = positionOffsets.find(l => l.level == level);
        const index = positionOffsets.indexOf(levelOffset);
        if (!levelOffset)
            levelOffset = { level, offset: 0 };
        else {
            // limit it so the last node ends in the middle
            if (increment && levelOffset.offset > (nodeLength / 2 - 1))
                return;
            if (!increment && levelOffset.offset < -(nodeLength / 2 - 1))
                return;
        }

        if (increment)
            levelOffset.offset++;
        else
            levelOffset.offset--;

        if (index > -1)
            positionOffsets[index] = levelOffset;
        else
            positionOffsets.push(levelOffset);


        this.setState({
            ...this.state,
            positionOffsets
        })

    }
    renderRow(nodes: ConversationTrackingTreeBaseNode[], conversationItems: SlimGenericContentItemModel[], level: number, prevIndex: number, prevLength: number) {
        if (!nodes || nodes.length == 0)
            return null;

        const selectedNodes = this.state.selectedNodes;
        const selectedNode = nodes.find(n => (n.conversationTrackingNodeType == "Content" && (selectedNodes[level] as ConversationTrackingTreeContentNode)?.contentId == (n as ConversationTrackingTreeContentNode).contentId)
            || (n.conversationTrackingNodeType == "SessionEnd" && selectedNodes[level]?.conversationTrackingNodeType == "SessionEnd"))
        const totalSessions = nodes.map(n => n.sessionIds?.length ?? 0).reduce((a, b) => (a + b));
        const nodeOffset = this.state.positionOffsets.find(o => o.level == level)?.offset ?? 0;
        const prevNodeOffset = this.state.positionOffsets.find(o => o.level == (level - 1))?.offset ?? 0;

        const treeRowOffset = css`
            margin-right: ${nodeOffset * 144 * 2}px;
        `
        return (
            <>
                <DownTreePaths prevIndex={prevIndex} prevLength={prevLength} activeIndex={nodes.indexOf(selectedNode)} nodes={nodes} nodeOffset={nodeOffset} prevNodeOffset={prevNodeOffset} />
                <div className="row-separator">
                    <div className="row-separator-body" />
                </div>
                <div className="pager">
                    <div className="pager-body">
                        <TreeRowPager
                            onRight={() => this.handlePage(level, true, nodes.length)}
                            onLeft={() => this.handlePage(level, false, nodes.length)}
                            canMoveLeft={nodeOffset > -(nodes.length / 2 - 1)}
                            canMoveRight={nodeOffset <= (nodes.length / 2 - 1)} />
                        <p className="pager-label">{selectedNode ? `${nodes.indexOf(selectedNode) + 1} of ` : '-- of '} {nodes.length.toString()} items</p>
                    </div>
                </div>
                <div className={`tree-row ${treeRowOffset}`}>
                    {nodes?.map((n, i) => {
                        if (n.conversationTrackingNodeType == "Content") {
                            const contentNode = n as ConversationTrackingTreeContentNode;
                            const selected = (selectedNodes[level] as ConversationTrackingTreeContentNode)?.contentId == contentNode.contentId;
                            return <ContentNode id={contentNode.contentId + level}
                                key={i}
                                nodeData={contentNode}
                                percentage={n.sessionIds.length / totalSessions}
                                number={n.sessionIds.length}
                                conversationItem={conversationItems.find(c => c.id == contentNode.contentId)}
                                selected={selected}
                                childSelected={selectedNodes[level + 1] != null}
                                active={selected || selectedNodes[level] == null}
                                isLoadingDetails={this.props.trackingContainer.state.isLoadingDetails}
                                onClick={() => this.handleContentNodeClick(contentNode, level)} />
                        }
                        if (n.conversationTrackingNodeType == "SessionEnd") {
                            return <SessionEndNode key={i}
                                number={n.sessionIds.length}
                                selected={selectedNodes[level]?.conversationTrackingNodeType == 'SessionEnd'}
                                percentage={n.sessionIds.length / totalSessions}
                                active={selectedNodes[level]?.conversationTrackingNodeType == 'SessionEnd' || selectedNodes[level] == null}
                                onClick={() => this.handleEndNodeClick(level, n)} />
                        }
                    })}
                </div>
                {selectedNode != null && selectedNode.conversationTrackingNodeType == "Content" && this.renderRow(_.sortBy(selectedNode.childNodes, n => (n.sessionIds?.length ?? 0)).reverse(), conversationItems, level + 1, nodes.indexOf(selectedNode), nodes.length)}
            </>
        )
    }
    render() {
        const { trackingContainer } = this.props;
        const tree = trackingContainer.findTree(this.props.applicationId, this.props.filter);
        if (trackingContainer.state.isLoading || !tree)
            return (<div className={loaderContainer}><SecondaryLoader /></div>)

        return (<div className={wrapperStyle}>
            <CustomScrollbars>
                {tree.data?.childNodes?.length > 0 ?
                    <div className="tree-container">
                        <div className="tree-row">
                            <SessionStartNode selected={this.state.selectedNodes.length > 0} onClick={this.handleStartNodeClick.bind(this)} active />
                        </div>
                        {this.renderRow(_.sortBy(tree.data.childNodes, n => (n.sessionIds?.length ?? 0)).reverse(), tree.conversationItems, 0, 0, 1)}
                    </div>
                    : <AnalyticsEmptyView />}
            </CustomScrollbars>
            {!this.props.isSpark ?
                <div className="conversation-preview-container">
                    <CustomScrollbars>
                        <ConversationScript nodes={this.state.selectedNodes ?? []} applicationId={this.props.applicationId} hasStart />
                    </CustomScrollbars>
                </div>
                : ""
            }
        </div>)
    }
}
const loaderContainer = css`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin: 64px 0;
    .spinner > div {
        background: ${color_colors_ocean};
    }

`
const wrapperStyle = css`
    display: flex;
    height: 100%;
    background: white;
    .tree-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 64px 0;
        overflow-x: hidden;
        margin-bottom: 64px;
    }
    .tree-row {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .row-separator {
        position: relative;
        width: 100%;
        .row-separator-body {
            height: 1px;
            bottom: 80px;
            left: 0;
            right: 0;
            position: absolute;
            border-top: ${style_border_default};
        }
    }
    .pager {
        position: relative;
        width: 100%;
        z-index: 6;
       
        .pager-body {
            bottom: 52px;
            right: 32;
            position: absolute;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            .pager-label {
                color: ${color_text_light};
                font-size: 10px;
                line-height: 16px;
                text-align: right;
                background: white;
            }
        }
    }

    .conversation-preview-container {
        background: ${color_shades_lighter};
        min-width: 576px;
        height: 100%;
        border-left: ${style_border_default};
    }
   
`

export default ApplicationTrackingPage;